/**
 * Landing-page "free, bring your own key" section.
 *
 * Sits between <PrivacySection> and <GetTheAppSection>: "your health data is
 * yours" flows straight into "and your AI key is yours too", and it lands
 * before the app-download ask.
 *
 * Copy discipline: this section renders on deployments where payments are off,
 * so it must contain no money vocabulary at all — no currency symbols, no
 * "credits", no processor names. The paywall-off DOM sweeps
 * (`frontend/e2e/health-assistant/paywall-off.spec.ts`,
 * `frontend/e2e/landing-byok.spec.ts`) fail the build if that slips. BYOK talk
 * is about keys, not money, so the constraint costs nothing.
 */

import { useTranslation } from "react-i18next";
import { Boxes, CircleCheck, Handshake, KeyRound, ShieldCheck } from "lucide-react";

import { useAuthSnapshot } from "@shared/lib/auth";
import { Button } from "@shared/ui/button";

import { localizedHref, resolveLang } from "../lib/links";

/** The key manager page (`apps/ai_services/urls.py` → `frontend_patterns`). */
const AI_KEYS_PATH = "/settings/ai-keys";

const ASSURANCES = [
  { key: "direct", Icon: Handshake },
  { key: "encrypted", Icon: ShieldCheck },
  { key: "providers", Icon: Boxes },
] as const;

export function ByokSection() {
  const { t, i18n } = useTranslation("landing");
  const lang = resolveLang(i18n.resolvedLanguage);
  // `status`, not `isAuthenticated` — the snapshot returns
  // `{ status, user, isLoading, refreshUser }`, so reading `isAuthenticated`
  // off it is always undefined.
  const { status } = useAuthSnapshot();
  const isAuthenticated = status === "authenticated";

  return (
    <section
      id="byok"
      className="bg-background px-4 py-16 sm:px-6 sm:py-20 lg:px-8"
      aria-labelledby="byok-heading"
    >
      <div className="mx-auto max-w-5xl">
        <div className="text-center">
          <p className="text-sm font-semibold uppercase tracking-[0.25em] text-primary">
            {t("byok.eyebrow")}
          </p>
          <h2
            id="byok-heading"
            className="mt-4 font-serif text-3xl font-semibold leading-[1.1] tracking-[-0.03em] text-foreground sm:text-4xl"
          >
            {t("byok.heading")}
          </h2>
          <p className="mx-auto mt-5 max-w-3xl text-lg leading-8 text-muted-foreground">
            {t("byok.subheading")}
          </p>
        </div>

        <div className="mt-10 rounded-3xl border border-border bg-card p-6 text-start shadow-sm sm:p-8">
          {/* dir="rtl" already flips a flex row on its own; adding a reverse
              utility here would flip it back and put the icon on the wrong
              side. Logical properties only. */}
          <div className="flex items-center gap-3">
            <KeyRound className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
            <h3 className="text-lg font-semibold text-foreground">
              {t("byok.keys.heading")}
            </h3>
          </div>
          <p className="mt-3 text-base leading-7 text-muted-foreground">
            {t("byok.keys.body")}
          </p>
        </div>

        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          {ASSURANCES.map(({ key, Icon }) => (
            <div
              key={key}
              data-testid={`byok-assurance-${key}`}
              className="rounded-2xl border border-border bg-card p-6 text-start shadow-sm transition-shadow hover:shadow-md"
            >
              <Icon className="h-6 w-6 text-primary" aria-hidden="true" />
              <h3 className="mt-4 font-semibold text-foreground">
                {t(`byok.assurances.${key}.title`)}
              </h3>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">
                {t(`byok.assurances.${key}.description`)}
              </p>
            </div>
          ))}
        </div>

        <div
          className="mt-6 rounded-2xl border border-dashed border-border bg-muted/40 p-6 text-start"
          data-testid="byok-no-key"
        >
          <div className="flex items-center gap-3">
            <CircleCheck className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
            <h3 className="font-semibold text-foreground">{t("byok.noKey.title")}</h3>
          </div>
          <p className="mt-3 text-sm leading-6 text-muted-foreground">
            {t("byok.noKey.description")}
          </p>
        </div>

        <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
          {isAuthenticated ? (
            <Button size="lg" asChild>
              <a href={localizedHref(AI_KEYS_PATH, lang)} data-testid="byok-cta-keys">
                <KeyRound className="h-4 w-4" aria-hidden="true" />
                {t("byok.cta.manageKeys")}
              </a>
            </Button>
          ) : (
            <>
              <Button size="lg" asChild>
                <a
                  href={localizedHref("/register", lang)}
                  data-testid="byok-cta-register"
                >
                  {t("byok.cta.primary")}
                </a>
              </Button>
              <a
                href={localizedHref(AI_KEYS_PATH, lang)}
                data-testid="byok-cta-keys"
                className="inline-flex min-h-[44px] items-center text-sm font-medium text-primary underline-offset-4 hover:underline"
              >
                {t("byok.cta.secondary")}
              </a>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

export const LandingByokSection = ByokSection;
export default ByokSection;
